import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Eye, EyeOff, ArrowRight, ArrowLeft, Sun, Moon, Phone, Shield, CheckCircle2, User, Building2 } from 'lucide-react';
import { auth } from '../firebase';
import { goToRedirect } from '../lib/redirect';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';

const LOGO_URL = '/logo.webp';

const RegisterPage = () => {
  const { register } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect');

  // B2C vs B2B mode
  const [mode, setMode] = useState('b2c');

  // Step: 1 = phone input, 2 = OTP verify, 3 = profile form
  const [step, setStep] = useState(1);

  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [firebaseToken, setFirebaseToken] = useState('');
  const [verifiedPhone, setVerifiedPhone] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    account_category: 'individual',
    gstin: '',
    // B2B fields
    company_name: '',
    billing_email: '',
    address: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const recaptchaContainerRef = useRef(null);
  const recaptchaVerifierRef = useRef(null);

  // Countdown timer for resend OTP
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const setupRecaptcha = () => {
    if (recaptchaVerifierRef.current) {
      return recaptchaVerifierRef.current;
    }

    const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
      size: 'invisible',
      callback: () => {},
      'expired-callback': () => {
        recaptchaVerifierRef.current = null;
      }
    });

    recaptchaVerifierRef.current = verifier;
    return verifier;
  };

  const handleSendOtp = async () => {
    setError('');
    const phone = phoneNumber.trim();

    if (!phone) {
      setError('Please enter your mobile number');
      return;
    }

    // Format phone with +91 if not present
    let formattedPhone = phone;
    if (!formattedPhone.startsWith('+')) {
      formattedPhone = '+91' + formattedPhone.replace(/^0+/, '');
    }

    if (formattedPhone.length < 12) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);
    try {
      if (!auth) {
        setError('Phone verification service is not available. Please try again later.');
        setLoading(false);
        return;
      }
      const appVerifier = setupRecaptcha();
      const result = await signInWithPhoneNumber(auth, formattedPhone, appVerifier);
      setConfirmationResult(result);
      setOtpSent(true);
      setStep(2);
      setCountdown(60);
      setVerifiedPhone(formattedPhone);
    } catch (err) {
      console.error('OTP send error:', err);
      // Reset recaptcha on error
      recaptchaVerifierRef.current = null;
      if (err.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later.');
      } else if (err.code === 'auth/invalid-phone-number') {
        setError('Invalid phone number. Please check and try again.');
      } else {
        setError(err.message || 'Failed to send OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const result = await confirmationResult.confirm(otp);
      const idToken = await result.user.getIdToken();
      setFirebaseToken(idToken);
      setStep(3);
    } catch (err) {
      console.error('OTP verify error:', err);
      if (err.code === 'auth/invalid-verification-code') {
        setError('Invalid OTP. Please check and try again.');
      } else if (err.code === 'auth/code-expired') {
        setError('OTP expired. Please request a new one.');
      } else {
        setError(err.message || 'OTP verification failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    recaptchaVerifierRef.current = null;
    setOtp('');
    setStep(1);
    setOtpSent(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (mode === 'b2c' && formData.account_category === 'business_gst' && !formData.gstin) {
      setError('GSTIN is required for Business with GST accounts');
      return;
    }

    if (mode === 'b2b' && !formData.company_name) {
      setError('Company name is required');
      return;
    }

    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      if (mode === 'b2b') {
        // B2B registration
        const res = await fetch(`${API_URL}/api/auth/register-b2b`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            company_name: formData.company_name,
            gstin: formData.gstin || null,
            billing_email: formData.billing_email || formData.email,
            contact_person: formData.name,
            address: formData.address || null,
            email: formData.email,
            phone: verifiedPhone,
            password: formData.password,
            firebase_token: firebaseToken,
          })
        });
        const data = await res.json();
        if (!res.ok) throw { response: { data } };

        // Store token and redirect
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        window.location.href = '/dashboard';
      } else {
        // B2C registration
        await register({
          name: formData.name,
          email: formData.email,
          phone: verifiedPhone,
          password: formData.password,
          user_type: 'b2c',
          account_category: formData.account_category,
          gstin: formData.account_category === 'business_gst' ? formData.gstin : null,
          firebase_token: firebaseToken,
          role: 'client'
        });
        goToRedirect(navigate, redirectUrl);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const stepIndicator = (
    <div className="flex items-center gap-2 mb-8">
      {[
        { num: 1, label: 'Phone', icon: Phone },
        { num: 2, label: 'Verify', icon: Shield },
        { num: 3, label: mode === 'b2b' ? 'Business' : 'Profile', icon: mode === 'b2b' ? Building2 : User },
      ].map((s, i) => {
        const Icon = s.icon;
        const isActive = step === s.num;
        const isCompleted = step > s.num;
        return (
          <React.Fragment key={s.num}>
            {i > 0 && (
              <div className={`flex-1 h-0.5 ${isCompleted ? 'bg-primary' : isDark ? 'bg-[#1e2128]' : 'bg-[#e5dfd0]'}`} />
            )}
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  isCompleted
                    ? 'bg-primary text-primary-foreground'
                    : isActive
                    ? 'bg-primary/20 text-primary ring-2 ring-primary'
                    : isDark
                    ? 'bg-[#13161a] text-muted-foreground'
                    : 'bg-[#faf8f3] text-muted-foreground'
                }`}
              >
                {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className={`text-xs ${isActive || isCompleted ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                {s.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  const inputClass = `h-12 ${
    isDark
      ? 'bg-[#13161a] border-[#1e2128] focus:border-primary'
      : 'bg-[#faf8f3] border-[#e5dfd0] focus:border-primary'
  } focus:ring-primary/20 placeholder:text-muted-foreground/50`;

  return (
    <div className={`min-h-screen flex ${isDark ? 'bg-[#0a0b0d]' : 'bg-[#f7f3e8]'}`}>
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_b8804bf8-27fc-4a61-8680-cf3a3c2f1596/artifacts/ysraqoo0_Clayworks%20Electronic%20City.webp"
            alt="Modern workspace"
            className="w-full h-full object-cover"
          />
          <div
            className={`absolute inset-0 ${
              isDark
                ? 'bg-gradient-to-br from-[#0a0b0d]/95 via-[#0a0b0d]/80 to-primary/30'
                : 'bg-gradient-to-br from-[#f7f3e8]/95 via-[#f7f3e8]/80 to-primary/20'
            }`}
          />
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Clayworks" className="h-10 w-auto" />
          </div>

          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className={`font-heading text-5xl font-bold leading-tight ${isDark ? 'text-white' : 'text-[#2d2a26]'}`}>
                Join the<br />
                <span className="text-primary">Future</span> of<br />
                Workspaces
              </h1>
              <p className={`text-lg max-w-md leading-relaxed ${isDark ? 'text-white/70' : 'text-[#5c5750]'}`}>
                Get instant access to premium meeting rooms, flexible desks, and event spaces. Start your journey today.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-primary" />
                </div>
                <span className={isDark ? 'text-white/80' : 'text-[#5c5750]'}>Verify your mobile number</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-primary" />
                </div>
                <span className={isDark ? 'text-white/80' : 'text-[#5c5750]'}>Confirm OTP in seconds</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <User className="w-4 h-4 text-primary" />
                </div>
                <span className={isDark ? 'text-white/80' : 'text-[#5c5750]'}>Complete your profile and start booking</span>
              </div>
            </div>
          </div>

          <div className={`text-sm ${isDark ? 'text-white/40' : 'text-[#9a958d]'}`}>
            &copy; {new Date().getFullYear()} Clayworks. All rights reserved.
          </div>
        </div>
      </div>

      {/* Right Side - Register Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md space-y-6">
          {/* Theme Toggle */}
          <div className="flex justify-end">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-all ${
                isDark ? 'hover:bg-[#13161a] text-muted-foreground hover:text-foreground' : 'hover:bg-[#efe9da] text-muted-foreground hover:text-foreground'
              }`}
              aria-label="Toggle theme"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-6">
            <img src={LOGO_URL} alt="Clayworks" className="h-10 w-auto" />
          </div>

          {/* Back Button */}
          <button
            onClick={() => {
              if (step > 1 && step < 3) {
                setStep(step - 1);
                setError('');
              } else {
                navigate('/login');
              }
            }}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="register-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            {step > 1 && step < 3 ? 'Back' : 'Back to login'}
          </button>

          {/* Header */}
          <div className="space-y-2">
            <h2 className="font-heading text-3xl font-bold" data-testid="register-heading">
              {step === 1 && 'Verify your phone'}
              {step === 2 && 'Enter OTP'}
              {step === 3 && (mode === 'b2b' ? 'Business details' : 'Complete your profile')}
            </h2>
            <p className="text-muted-foreground">
              {step === 1 && 'We\'ll send a one-time password to verify your number'}
              {step === 2 && `OTP sent to ${verifiedPhone}`}
              {step === 3 && (mode === 'b2b' ? 'Set up your company account' : 'Just a few more details to get started')}
            </p>
          </div>

          {/* B2C / B2B Toggle - only on step 1 */}
          {step === 1 && (
            <div className={`flex rounded-lg p-1 ${isDark ? 'bg-[#13161a]' : 'bg-[#efe9da]'}`}>
              <button
                onClick={() => setMode('b2c')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition-all ${
                  mode === 'b2c'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="register-mode-b2c"
              >
                <User className="w-4 h-4" />
                Individual
              </button>
              <button
                onClick={() => setMode('b2b')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition-all ${
                  mode === 'b2b'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="register-mode-b2b"
              >
                <Building2 className="w-4 h-4" />
                Business
              </button>
            </div>
          )}

          {/* Step Indicator */}
          {stepIndicator}

          {/* Error */}
          {error && (
            <Alert variant="destructive" className="bg-destructive/10 border-destructive/30">
              <AlertDescription data-testid="register-error">{error}</AlertDescription>
            </Alert>
          )}

          {/* Step 1: Phone Number */}
          {step === 1 && (
            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium">Mobile number</Label>
                <div className="flex gap-2">
                  <div className={`h-12 flex items-center px-3 rounded-md border text-sm font-medium ${
                    isDark ? 'bg-[#13161a] border-[#1e2128] text-foreground' : 'bg-[#faf8f3] border-[#e5dfd0] text-foreground'
                  }`}>
                    +91
                  </div>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="98765 43210"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    data-testid="register-phone-input"
                    className={`flex-1 ${inputClass}`}
                    maxLength={10}
                  />
                </div>
              </div>

              <Button
                onClick={handleSendOtp}
                className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-medium text-base shadow-lg shadow-primary/25 transition-all hover:shadow-xl hover:shadow-primary/30"
                disabled={loading || phoneNumber.length < 10}
                data-testid="register-send-otp-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>
                    Send OTP
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="otp" className="text-sm font-medium">Enter 6-digit OTP</Label>
                <Input
                  id="otp"
                  type="text"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  data-testid="register-otp-input"
                  className={`text-center text-2xl tracking-[0.5em] font-mono ${inputClass}`}
                  maxLength={6}
                  autoFocus
                />
              </div>

              <Button
                onClick={handleVerifyOtp}
                className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-medium text-base shadow-lg shadow-primary/25 transition-all hover:shadow-xl hover:shadow-primary/30"
                disabled={loading || otp.length !== 6}
                data-testid="register-verify-otp-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>
                    Verify OTP
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>

              <div className="text-center">
                {countdown > 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Resend OTP in <span className="text-foreground font-medium">{countdown}s</span>
                  </p>
                ) : (
                  <button
                    onClick={handleResendOtp}
                    className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
                    data-testid="register-resend-otp-btn"
                  >
                    Didn't receive? Resend OTP
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Profile Form */}
          {step === 3 && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className={`flex items-center gap-3 p-3 rounded-lg ${isDark ? 'bg-green-500/10 border border-green-500/20' : 'bg-green-50 border border-green-200'}`}>
                <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
                <span className="text-sm text-green-600 dark:text-green-400">
                  Phone verified: {verifiedPhone}
                </span>
              </div>

              {/* B2B: Company fields */}
              {mode === 'b2b' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="company_name" className="text-sm font-medium">Company name *</Label>
                    <Input
                      id="company_name"
                      type="text"
                      placeholder="Acme Corporation"
                      value={formData.company_name}
                      onChange={(e) => handleChange('company_name', e.target.value)}
                      required
                      data-testid="register-company-name-input"
                      className={inputClass}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="gstin" className="text-sm font-medium">GSTIN (optional)</Label>
                    <Input
                      id="gstin"
                      type="text"
                      placeholder="22AAAAA0000A1Z5"
                      value={formData.gstin}
                      onChange={(e) => handleChange('gstin', e.target.value.toUpperCase())}
                      data-testid="register-gstin-input"
                      className={inputClass}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="billing_email" className="text-sm font-medium">Billing email</Label>
                    <Input
                      id="billing_email"
                      type="email"
                      placeholder="accounts@company.com"
                      value={formData.billing_email}
                      onChange={(e) => handleChange('billing_email', e.target.value)}
                      data-testid="register-billing-email-input"
                      className={inputClass}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address" className="text-sm font-medium">Office address</Label>
                    <Input
                      id="address"
                      type="text"
                      placeholder="123 Business Park, Bangalore"
                      value={formData.address}
                      onChange={(e) => handleChange('address', e.target.value)}
                      data-testid="register-address-input"
                      className={inputClass}
                    />
                  </div>

                  <div className={`h-px ${isDark ? 'bg-[#1e2128]' : 'bg-[#e5dfd0]'}`} />
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Your account details</p>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">{mode === 'b2b' ? 'Contact person name' : 'Full name'}</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  data-testid="register-name-input"
                  className={inputClass}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@company.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  data-testid="register-email-input"
                  className={inputClass}
                />
              </div>

              {/* B2C only: Account type selector */}
              {mode === 'b2c' && (
                <>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Account type</Label>
                    <Select value={formData.account_category} onValueChange={(value) => handleChange('account_category', value)}>
                      <SelectTrigger data-testid="register-account-type-select" className={`h-12 ${
                        isDark
                          ? 'bg-[#13161a] border-[#1e2128] focus:border-primary'
                          : 'bg-[#faf8f3] border-[#e5dfd0] focus:border-primary'
                      } focus:ring-primary/20`}>
                        <SelectValue placeholder="Select account type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="individual">Individual</SelectItem>
                        <SelectItem value="business_gst">Business (with GST)</SelectItem>
                        <SelectItem value="business_no_gst">Business (without GST)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.account_category === 'business_gst' && (
                    <div className="space-y-2">
                      <Label htmlFor="gstin_b2c" className="text-sm font-medium">GSTIN</Label>
                      <Input
                        id="gstin_b2c"
                        type="text"
                        placeholder="22AAAAA0000A1Z5"
                        value={formData.gstin}
                        onChange={(e) => handleChange('gstin', e.target.value.toUpperCase())}
                        data-testid="register-gstin-input"
                        className={inputClass}
                      />
                    </div>
                  )}
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a strong password"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    required
                    data-testid="register-password-input"
                    className={`pr-12 ${inputClass}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium">Confirm password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  required
                  data-testid="register-confirm-password-input"
                  className={inputClass}
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-medium text-base shadow-lg shadow-primary/25 transition-all hover:shadow-xl hover:shadow-primary/30"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {mode === 'b2b' ? 'Create Business Account' : 'Create Account'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>

              <p className="text-xs text-muted-foreground/60 text-center">
                By creating an account, you agree to our Terms of Service and Privacy Policy
              </p>
            </form>
          )}

          {/* reCAPTCHA container - invisible */}
          <div id="recaptcha-container" ref={recaptchaContainerRef}></div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
